import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { getCalendar, saveCalendar } from "../api";
import { countWorkDays } from "../constants";
import { daysInMonth, weekdayMon0, ddKey, todayParts } from "../utils/dates";
import AppPage from "../components/AppPage";

const MONTH_KEYS = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];
const WD_KEYS = ["mon","tue","wed","thu","fri","sat","sun"];

function generateDefaultDays(year, month, weekType) {
  const total = daysInMonth(year, month);
  const days = {};
  for (let d = 1; d <= total; d++) {
    const wd = weekdayMon0(year, month, d);
    if (weekType === 6) days[ddKey(d)] = wd === 6 ? "weekend" : "work";
    else days[ddKey(d)] = wd >= 5 ? "weekend" : "work";
  }
  return days;
}

export default function CalendarPage() {
  const { t } = useTranslation();
  const { user, company } = useAuth();
  const today = todayParts();
  const [year, setYear] = useState(today.year);
  const [month, setMonth] = useState(today.month);
  const [days, setDays] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const TYPES = [
    { value: "work",    label: t("calendar_page.type_work"),    color: "#198754" },
    { value: "weekend", label: t("calendar_page.type_weekend"), color: "#adb5bd" },
    { value: "holiday", label: t("calendar_page.type_holiday"), color: "#dc3545" },
    { value: "short",   label: t("calendar_page.type_short"),   color: "#fd7e14" },
  ];

  useEffect(() => {
    (async () => {
      const cal = await getCalendar(year, month);
      setDays(cal.days || {});
    })();
  }, [year, month]);

  const total = daysInMonth(year, month);

  const onChangeDay = (d, value) => setDays({ ...days, [ddKey(d)]: value });

  const onSave = async () => {
    setBusy(true); setMsg("");
    try {
      await saveCalendar(year, month, days);
      setMsg(t("calendar_page.saved"));
    } finally { setBusy(false); }
  };

  const onResetDefault = () => setDays(generateDefaultDays(year, month, user?.week_type || 5));
  const norm = countWorkDays(days);

  return (
    <AppPage testId="calendar-page" bodyScroll="scroll">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <Link to="/timesheet" className="small text-muted text-decoration-none d-inline-flex align-items-center mb-1"
                data-testid="cal-back-timesheet">
            <i className="bi bi-arrow-left me-1"></i>{t("nav.timesheet")}
          </Link>
          <h4 className="fw-bold mb-0">{t("calendar_page.title")}</h4>
        </div>
        <div className="d-flex gap-2">
          <select className="form-select" style={{ width: 160 }} value={month}
                  onChange={(e) => setMonth(Number(e.target.value))} data-testid="cal-month">
            {MONTH_KEYS.map((mk, i) => <option key={i} value={i}>{t(`months.${mk}`)}</option>)}
          </select>
          <select className="form-select" style={{ width: 110 }} value={year}
                  onChange={(e) => setYear(Number(e.target.value))}>
            {[year - 1, year, year + 1].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {msg && <div className="alert alert-success py-2">{msg}</div>}

      <div className="card card-soft mb-3"><div className="card-body">
        <div className="d-flex flex-wrap gap-3 mb-3 small">
          <div><strong>{t("calendar_page.company")}:</strong> {company?.name}</div>
          <div><strong>{t("calendar_page.norm_days")}:</strong> <span data-testid="cal-norm">{norm}</span></div>
          <div><strong>{t("calendar_page.days_in_month")}:</strong> {total}</div>
        </div>

        <div className="row row-cols-2 row-cols-md-4 row-cols-lg-7 g-2">
          {Array.from({ length: total }, (_, i) => i + 1).map(d => {
            const wd = weekdayMon0(year, month, d);
            const status = days[ddKey(d)] || "work";
            const tp = TYPES.find(x => x.value === status);
            return (
              <div key={d} className="col">
                <div className="border rounded p-2"
                     style={{ background: status === "weekend" ? "#f1f3f6" : status === "holiday" ? "#ffe7e7" : status === "short" ? "#fff4e0" : "white" }}>
                  <div className="d-flex justify-content-between align-items-center">
                    <strong>{d}</strong>
                    <span className="text-muted small">{t(`weekdaysShort.${WD_KEYS[wd]}`)}</span>
                  </div>
                  <select className="form-select form-select-sm mt-1" value={status}
                          onChange={(e) => onChangeDay(d, e.target.value)}
                          data-testid={`cal-day-${d}`}
                          style={{ borderLeft: `4px solid ${tp.color}` }}>
                    {TYPES.map(x => <option key={x.value} value={x.value}>{x.label}</option>)}
                  </select>
                </div>
              </div>
            );
          })}
        </div>
      </div></div>

      <div className="d-flex gap-2">
        <button className="btn btn-primary" onClick={onSave} disabled={busy} data-testid="cal-save">
          <i className="bi bi-check-circle me-1"></i> {t("calendar_page.save")}
        </button>
        <button className="btn btn-outline-secondary" onClick={onResetDefault}>
          <i className="bi bi-arrow-clockwise me-1"></i> {t("calendar_page.reset_default")}
        </button>
      </div>
    </AppPage>
  );
}
